import axios, { AxiosError } from 'axios'
import type { ApiError } from './types'
import { useAuthStore } from '../auth/authStore'

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? '/api/v1',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
})

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    // 401: el token venció o fue invalidado. Se cierra la sesión local para
    // que el router mande al login en lugar de dejar la UI en un estado roto.
    // El 403 NO cierra sesión: puede ser una acción puntual sin permisos.
    if (error.response?.status === 401) {
      useAuthStore.getState().clear()
    }
    return Promise.reject(error)
  },
)

/** Mensaje legible de un error de la API, listo para mostrar al usuario. */
export function errorMessage(error: unknown, fallback = 'Ocurrió un error inesperado.'): string {
  if (!axios.isAxiosError(error)) return fallback

  const data = error.response?.data as ApiError | undefined

  // Errores de validación: se muestra el primero, que es el más accionable.
  const firstValidationError = data?.errors && Object.values(data.errors)[0]?.[0]
  if (firstValidationError) return firstValidationError

  if (data?.message) return data.message
  if (error.code === 'ERR_NETWORK') return 'No se pudo conectar con el servidor.'

  return fallback
}
